import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import {
  Home,
  BookOpen,
  BarChart3,
  MessageSquare,
  Award,
  LogOut,
  Moon,
  Sun,
  LayoutDashboard,
  GraduationCap,
  User as UserIcon,
  HelpCircle,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";

const menuItems = [
  { title: "マイページ", url: "/student/dashboard", icon: Home },
  { title: "コース", url: "/student/courses", icon: BookOpen },
  { title: "進捗", url: "/student/progress", icon: BarChart3 },
  { title: "AI講師", url: "/student/chat", icon: MessageSquare },
  { title: "達成", url: "/student/achievements", icon: Award },
  { title: "ヘルプ", url: "/docs", icon: HelpCircle },
];

export function StudentSidebar() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  const isAdminView = location.pathname.startsWith('/dashboard') ||
                      location.pathname.startsWith('/students') ||
                      location.pathname.startsWith('/progress') ||
                      location.pathname.startsWith('/analytics') ||
                      location.pathname.startsWith('/chat') ||
                      location.pathname.startsWith('/videos') ||
                      location.pathname.startsWith('/escalations');
  const canSwitchView = user?.role === 'admin' || user?.role === 'educator';
  const switchButtonLabel = isAdminView ? "学習者ページへ移動" : "管理者ページへ戻る";

  const handleViewSwitch = () => {
    if (isAdminView) {
      navigate('/student/dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  const handleLogout = async () => {
    // Clear chat session flag before logout
    sessionStorage.removeItem('chat_page_visited');

    const { error } = await supabase.auth.signOut();

    if (error) {
      toast({
        title: "エラー",
        description: "ログアウトに失敗しました",
        variant: "destructive",
      });
    } else {
      toast({
        title: "ログアウト成功",
        description: "またお会いしましょう",
      });
      navigate("/auth");
    }
  };

  return (
    <Sidebar
      className={isCollapsed ? "w-14" : "w-60"}
      collapsible="icon"
      role="navigation"
      aria-label="学習メニュー"
    >
      <SidebarContent>
        <SidebarGroup>
          <div className="flex items-center justify-between px-2 py-2">
            <SidebarGroupLabel className={isCollapsed ? "sr-only" : ""}>
              学習メニュー
            </SidebarGroupLabel>
            <SidebarTrigger className="ml-auto" />
          </div>
          <SidebarGroupContent>
            <SidebarMenu role="menubar" aria-label="メニュー">
              {menuItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      className={({ isActive }) =>
                        isActive
                          ? "bg-muted text-primary font-medium"
                          : "hover:bg-muted/50"
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon className="h-4 w-4" aria-hidden="true" />
                          {!isCollapsed && <span>{item.title}</span>}
                          {isActive && <span className="sr-only">（現在のページ）</span>}
                        </>
                      )}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <div className="p-2 space-y-2">
          {!isCollapsed && (
            <>
              {/* User Info */}
              <div className="flex items-center gap-2 px-2 py-1">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground truncate flex-1">
                  {user?.email}
                </span>
              </div>

              {/* View Switch Button */}
              {canSwitchView && (
                <Button
                  variant={isAdminView ? "default" : "outline"}
                  size="sm"
                  onClick={handleViewSwitch}
                  className="w-full gap-2 justify-start"
                  aria-label={switchButtonLabel}
                >
                  {isAdminView ? (
                    <>
                      <GraduationCap className="h-4 w-4" aria-hidden="true" />
                      <span>{switchButtonLabel}</span>
                    </>
                  ) : (
                    <>
                      <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                      <span>{switchButtonLabel}</span>
                    </>
                  )}
                </Button>
              )}

              {/* Theme Switcher */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 justify-start"
                    aria-label={theme === "dark" ? "ダークモード（クリックで変更）" : "ライトモード（クリックで変更）"}
                  >
                    {theme === "dark" ? (
                      <>
                        <Moon className="h-4 w-4" aria-hidden="true" />
                        <span>ダークモード</span>
                      </>
                    ) : (
                      <>
                        <Sun className="h-4 w-4" aria-hidden="true" />
                        <span>ライトモード</span>
                      </>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="top">
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    ライトモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    ダークモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    システム設定
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Logout Button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="w-full gap-2 justify-start text-destructive hover:text-destructive"
                aria-label="ログアウト"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                <span>ログアウト</span>
              </Button>

              {/* Legal Links */}
              <Separator />
              <div className="flex justify-center gap-4 text-xs text-muted-foreground">
                <Link to="/terms" className="hover:text-primary underline">
                  利用規約
                </Link>
                <Link to="/privacy" className="hover:text-primary underline">
                  プライバシー
                </Link>
              </div>
            </>
          )}

          {isCollapsed && (
            <div className="flex flex-col gap-1">
              {/* View Switch (icon only) */}
              {canSwitchView && (
                <Button
                  variant={isAdminView ? "default" : "outline"}
                  size="icon"
                  onClick={handleViewSwitch}
                  title={switchButtonLabel}
                  aria-label={switchButtonLabel}
                >
                  {isAdminView ? (
                    <GraduationCap className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <LayoutDashboard className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              )}

              {/* Theme Switcher (icon only) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label={theme === "dark" ? "ダークモード（クリックで変更）" : "ライトモード（クリックで変更）"}
                  >
                    {theme === "dark" ? (
                      <Moon className="h-4 w-4" aria-hidden="true" />
                    ) : (
                      <Sun className="h-4 w-4" aria-hidden="true" />
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" side="right">
                  <DropdownMenuItem onClick={() => setTheme("light")}>
                    ライトモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("dark")}>
                    ダークモード
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme("system")}>
                    システム設定
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Logout (icon only) */}
              <Button
                variant="ghost"
                size="icon"
                onClick={handleLogout}
                title="ログアウト"
                aria-label="ログアウト"
                className="text-destructive hover:text-destructive"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          )}
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
